import React, { useRef, useCallback, useState, useEffect } from "react";
import { ReactTerminal } from "react-terminal";
import { CommandMap, useCommandMap } from "./hooks/useCommandMap";
import VideoPlayer from "./video/VideoPlayer";
import { split as shexSplit } from "shlex";
import StatusBar from "./StatusBar";
import styled from "styled-components";
import { setupWebGLPipeline } from "./webglUtils";

const CliVideo = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );
  const [src, setSrc] = useState<string>(
    // "https://sample.vodobox.net/skate_phantom_flex_4k/skate_phantom_flex_4k.m3u8"
    "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8"
    // "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/WhatCarCanYouGetForAGrand.mp4"
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!videoElement || !canvas) return;

    setupWebGLPipeline(videoElement, canvas);
  }, [videoElement, canvasRef]);

  const commandMap: CommandMap = useCommandMap(videoElement, setSrc);

  const processCommand = useCallback(
    async (command: string) => {
      const trimmedCommandAndArgs = shexSplit(command.trim());
      const [trimmedCommand, ...args] = trimmedCommandAndArgs;

      if (Object.keys(commandMap).includes(trimmedCommand)) {
        console.log(trimmedCommand, args);
        return await commandMap[trimmedCommand](...args);
      } else return `default;no op`;
    },
    [commandMap]
  );

  return (
    <MainWrapper>
      <VideoPlayer
        ref={videoRef}
        src={src}
        style={{
          boxSizing: "border-box",
          flexGrow: 2,
          display: "none",
        }}
        onVideoElementChange={setVideoElement}
        crossOrigin={"anonymous"}
      />
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} />
      </CanvasWrapper>
      <TerminalWrapper>
        <StatusBar videoElement={videoElement} src={src} />
        <ReactTerminal
          showControlBar={false}
          welcomeMessage={
            <WelcomeMessage>
              Welcome to stakra, a command-line video player. Type 'man' to get
              a list of commands
            </WelcomeMessage>
          }
          defaultHandler={async (
            command: keyof CommandMap,
            args: string | number
          ) => {
            const compositeCommand = `${command} ${args}`;
            const compositeCommandList = compositeCommand.split("||");
            const outputs = [];

            for (const cmd of compositeCommandList) {
              const result = await processCommand(cmd);
              // outputs.push(`${JSON.stringify(result) ?? ""}`);
              outputs.push(result);
            }

            return outputs.filter((e) => e !== "").join(`\n`);
          }}
          theme={"material-dark"}
        />
      </TerminalWrapper>
    </MainWrapper>
  );
};

const MainWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 60% 40%;
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
`;

const StyledCanvas = styled.canvas`
  box-sizing: border-box;
  height: 100%;
`;

const WelcomeMessage = styled.p`
  color: rgb(180, 210, 115);
  margin: 0;
  font-size: 18px;
  line-height: 22px;
`;

const CanvasWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #000;
  justify-content: center;
  box-sizing: border-box;
`;

const TerminalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  width: 100%;
  white-space: pre-wrap;
  background: rgb(21, 21, 21);
`;

export default CliVideo;
